import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowRight, ClipboardList, DoorOpen, User, Users } from 'lucide-react'
import Modal from './Modal'
import { addDays, formatFullDate, toMinutes } from '../api/calendar'
import { clockTime, meridiem } from '../api/attendance'
import type { Conflict, ConflictSide } from '../api/bookings'
import { collisionsOf, type ConflictRow } from './ConflictsView'
import { useCan, useSession } from '../useSession'
import AssignTaskDialog from './AssignTaskDialog'
import MoveRoomPanel from './MoveRoomPanel'
import AskTheFamily from './AskTheFamily'

/**
 * What a clash actually <em>is</em>, opened from its row (D-567).
 *
 * <b>The page could only ever say "that's fine" or "needs fixing"</b>, which
 * the owner reported as the thing making it hard to work: <i>"only being able
 * to say its fine or needs fixing makes that page difficult to manage... in my
 * head clicking on a dancer conflict row might pull up a modal that shows the
 * conflict on the calendar."</i> A verdict is a judgement, and nothing on the
 * screen let her look at what she was judging.
 *
 * <b>A modal rather than a navigation</b>, because the list is a work queue:
 * she is going down it, and leaving loses her week, her scroll and the
 * unanswered rows below. What the modal offers instead is a way <i>out</i> to
 * the screen that already shows this properly — a dancer's evening, a
 * teacher's day, a room's grid — none of which is new work, because all three
 * already exist.
 *
 * <b>A row is an arrangement, not a night</b> (D-150). The same two classes
 * clash every week they both run, so the strip below is drawn for the first
 * date and the rest are named. That is also why nothing here offers to fix one
 * evening: a fix aimed at a Tuesday leaves the row exactly where it was.
 */
export default function ConflictDetail({
  row,
  onClose,
}: {
  row: ConflictRow
  onClose: () => void
}) {
  // What the destination for a dancer row asks for, which this screen does
  // not (D-567). Asked here because this is where the kind is known.
  const mayViewPeople = useCan('ViewPeople')

  // What moving a class into another room requires, which this screen does
  // not (D-569). Same reason, one control over.
  const mayManageClasses = useCan('ManageClasses')

  /*
   * Handing it on is a staff act, and the same test the asks screen makes
   * of its own Assign a task control - a task is put on a colleague list, so
   * somebody with no staff record has nobody to put it on (D-549).
   */
  const { session } = useSession()
  const mayRaise = Boolean(session?.staffId)
  const [handing, setHanding] = useState(false)

  const first = row.firstSide
  const second = row.secondSide

  /*
   * <b>What this pair breaks, all of it</b> (D-609). The dialog used to be
   * told by `row.kind`, which is now only the collision the row *leads* with —
   * so a pair clashing on a teacher and a room would have led with the teacher
   * and quietly dropped the one control this screen actually has. Every
   * sentence and every control below asks the set, never the headline.
   */
  const collisions = collisionsOf(row)
  const collidesOn = (kind: Conflict['kind']) => collisions.some((c) => c.kind === kind)

  return (
    <Modal
      onDismiss={onClose}
      labelledBy="conflict-detail-heading"
      panelClassName="max-h-[90vh] w-full max-w-[32rem] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 sm:rounded-2xl"
    >
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {/*
            Every collision in the eyebrow (D-609), so the dialog cannot say
            "Teacher" over a pair that is also two classes in one room. The
            heading stays the one subject the row leads with - a dialog needs a
            name, and a dancer is the name worth leading with when there is one.
          */}
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-bold tracking-[0.14em] text-accent uppercase">
            {collisions.map((c) => (
              <span key={`${c.kind}:${c.subjectId}`} className="flex items-center gap-1.5">
                <KindIcon kind={c.kind} />
                {KIND_WORD[c.kind]}
              </span>
            ))}
          </p>
          <h2 id="conflict-detail-heading" className="mt-1 font-display text-xl wrap-anywhere">
            {row.subject}
          </h2>
          {collisions.length > 1 && (
            <p className="mt-0.5 text-[12.5px] wrap-anywhere text-ink-2">
              and {collisions.filter((c) => c.subjectId !== row.subjectId).map((c) => c.subject).join(', ')}
            </p>
          )}
          <p className="tabular mt-0.5 text-[12.5px] text-ink-3">
            {formatFullDate(row.dates[0])}
            {row.dates.length > 1 && ` and ${row.dates.length - 1} more`}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg px-2 py-1 text-[12.5px] font-semibold text-ink-3 hover:bg-surface-2 hover:text-ink"
        >
          Close
        </button>
      </header>

      <Strip first={first} second={second} />

      <ul className="mt-3 flex flex-col gap-2">
        <Side side={first} />
        <Side side={second} />
      </ul>

      {/*
        The honest sentence for each kind, and it is different in each case.
        A dancer clash cannot be fixed here at all — the answer is an enrolment
        change and Jackrabbit owns those (D-111) — so saying so is the useful
        thing rather than offering a control that would lie.
      */}
      <div className="mt-3 flex flex-col gap-2">
        {collisions.map((c) => (
          <p
            key={`${c.kind}:${c.subjectId}`}
            className="rounded-lg bg-surface-2 px-3 py-2.5 text-[12.5px] text-ink-2"
          >
            {/*
              Named, because one sentence for a pair that breaks two ways
              would have to describe both and would say neither well. Each
              collision is a different conversation and a different fix.
            */}
            {collisions.length > 1 && (
              <strong className="font-semibold text-ink">{c.subject}. </strong>
            )}
            {ADVICE[c.kind]}
          </p>
        ))}
      </div>

      {/*
        <b>The one fix this screen can make</b> (D-569). A dancer clash is an
        enrolment and Jackrabbit owns those; a teacher clash is a judgement
        about who takes what. Where a class meets on a given night is the
        studio's own, so the room conflict is the only one that gets a control
        rather than a sentence.

        Behind `ManageClasses`, which is what the endpoint requires — the
        screen itself is `ViewAllSchedules`, and those are granted separately
        (D-5), so a control offered to everyone here would be refused for some
        of them.
      */}
      {collidesOn('Room') && mayManageClasses && <MoveRoomPanel row={row} />}

      <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-line pt-3.5">
        {/*
          <b>Handing it to somebody, through the door that already exists</b>
          (D-568, applying A-17). A conflict given to a teacher is a task: it
          lands on `/asks`, it is reminded about, and it carries its answer —
          none of which needed a new kind. The dialog opens with the sentence
          already written, and she can change every word of it.

          Only where she can assign one at all, tested the same way `/asks`
          tests its own <i>Assign a task</i> — a staff record, because a task
          goes onto a colleague's list and somebody without one has no
          colleagues to put it on.

          It nests a dialog inside a dialog, which is the shape D-515's shell
          was built to stack: Escape answers the top one, and backing out of
          the task leaves the clash open behind it.
        */}
        {mayRaise && (
          <button
            type="button"
            onClick={() => setHanding(true)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
          >
            <ClipboardList size={14} strokeWidth={2.5} className="shrink-0" />
            Ask a colleague
          </button>
        )}

        {/*
          <b>Asking the family, which is the only place the answer is</b>
          (D-570). The detector cannot tell a dancer leaving technique early
          for her solo from one double-booked by mistake, and neither can the
          studio — her mother can, and until now there was no way to ask that
          left a trace.

          <b>A question rather than a task</b>, so it is a kind on the envelope
          (A-17): it wants an answer back from outside the building, which a
          task does not. It inherits the list, the reminders, the answer trail
          and the refusal to ask the same family the same thing twice.

          Only on a dancer clash. A teacher or a room clash is not a family's
          business, and offering it there would be asking a parent about the
          studio's own arrangements.
        */}
        {/*
          One per askable family, not one per row (D-609). Two overlapping
          classes commonly share several dancers; before the regrouping each
          had her own row and her own button, and a single button on the folded
          row would both hide the second household and leave the first
          unnamed - a control that does less than the row above it says.
        */}
        {mayRaise &&
          collisions
            .filter((c) => c.kind === 'Dancer' && c.canBeAsked)
            .map((c) => (
              <AskTheFamily key={c.subjectId} row={row} dancer={c} />
            ))}

        {/*
          Still asked, though every kind answers yes today (D-606). The
          question — may this reader follow this row — is the one D-333 says to
          decide where the permissions are known, and a destination added later
          is where it starts mattering again.
        */}
        {mayFollow(row.kind, mayViewPeople) && (
          <Link
            to={destination(row)}
            className="ml-auto inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
          >
            {OPEN_WORD[row.kind]}
            <ArrowRight size={14} strokeWidth={2.5} className="shrink-0" />
          </Link>
        )}
      </div>

      {handing && (
        <AssignTaskDialog onClose={() => setHanding(false)} suggested={taskFor(row)} />
      )}
    </Modal>
  )
}

/**
 * The sentence a conflict hands to whoever is asked to look at it (D-568).
 *
 * <b>Written here rather than by the person asking</b>, because everything in
 * it is already on the row and re-typing it is the tax that stops a control
 * like this being used. She can edit every word.
 *
 * <b>The note carries the whole clash, including the nights.</b> A task has a
 * title and a note and no subject of its own, so the note is the only place
 * the detail can live — and a teacher who opens `/asks` a week later has
 * nothing else to go on. Naming the dates matters most: the row is an
 * arrangement, not a night (D-150), and "every Tuesday" is a different job
 * from "this Tuesday".
 */
export function taskFor(row: ConflictRow): {
  title: string
  note: string
  dueOn: string
} {
  /*
   * <b>Two is the common case and it was the broken one.</b> The screen shows
   * one week, so a weekly pairing collapses to one date and a twice-weekly to
   * two — and *"and 1 other nights"* was what two produced. The header eight
   * lines up already says "and N more"; this diverged from it in the same
   * file.
   */
  const others = row.dates.length - 1
  const when =
    others === 0
      ? formatFullDate(row.dates[0])
      : `${formatFullDate(row.dates[0])} and ${others} other night${others === 1 ? '' : 's'}`

  return {
    /*
     * <b>Every way the pair clashes, not just the headline</b> (D-609). The
     * task is the only thing here that leaves the screen — a colleague reads
     * the title with none of this dialog around it — so *"the teacher clash
     * for Ashley A."* about a pair that is also two classes in one room sends
     * her to fix half of it.
     */
    title: `Look at the ${collisionsOf(row)
      .map((c) => `${KIND_NOUN[c.kind]} clash for ${c.subject}`)
      .join(' and the ')}`,
    /*
     * Composed here rather than from `row.description`, which the server
     * already writes as nearly this sentence. The difference is deliberate:
     * that one names one date because it describes one occurrence, and a
     * person asked to look needs to know whether this is every week. Said out
     * loud because two spellings of one sentence otherwise read as an
     * oversight.
     */
    note:
      `${row.firstSide.label} and ${row.secondSide.label} overlap ${row.overlap} ` +
      `on ${when}. Seen on the conflicts screen.`,
    /*
     * <b>Due the day before it next happens, not the usual week out.</b> A
     * task defaults to seven days (D-554), and the conflicts screen only ever
     * shows the current week — so the default lands *on or after* the last
     * clash night in view, and `TaskKind` reminds on the closing date. The
     * person asked would have been reminded the morning after the night they
     * were asked to look at.
     */
    dueOn: addDays(row.dates[0], -1),
  }
}

const KIND_NOUN = {
  Dancer: 'dancer',
  Teacher: 'teacher',
  Room: 'room',
} as const

const KIND_WORD = {
  Dancer: 'Dancer conflict',
  Teacher: 'Teacher conflict',
  Room: 'Room conflict',
} as const

const OPEN_WORD = {
  Dancer: 'Open her evening',
  Teacher: 'Open the day',
  Room: 'Open the room grid',
} as const

const ADVICE = {
  Dancer:
    'Leaving one class early for another is normal here, and the app cannot tell that from a ' +
    'mistake — which is why it asks rather than guesses. Changing it means changing an ' +
    'enrolment, and that happens in Jackrabbit.',
  Teacher:
    'One teacher, two places. The day view shows both blocks side by side, which is usually ' +
    'enough to see whether it is a mistake or a block she takes on purpose.',
  Room:
    'Two things in one room. The day grid shows what else that room is doing, and what is ' +
    'free around it.',
} as const

function KindIcon({ kind }: { kind: Conflict['kind'] }) {
  const props = { size: 13, strokeWidth: 2.5 as const }

  if (kind === 'Dancer') return <Users {...props} />
  if (kind === 'Teacher') return <User {...props} />
  return <DoorOpen {...props} />
}

/**
 * Where this row is really looked at, which is a screen that already exists.
 *
 * <b>Decided here, where the kind is known, rather than in the browser</b> —
 * one row can be about a dancer, a teacher or a room, and a link that guesses
 * lands somebody on a screen that cannot answer them.
 *
 * <b>All three land inside one capability, and that is recent</b> (D-606).
 * `/conflicts`, the teacher day and the day grid are all `ViewAllSchedules`.
 * The dancer row used to go to `/find`, which is `ViewPeople` — D-5 grants
 * those separately, so somebody holding the first without the second reached
 * this screen, opened a dancer row and was refused by the destination. That
 * was D-333 exactly, and `mayFollow` existed to hide the link. Moving the
 * destination removed the mismatch instead, which is the better fix.
 */
export function destination(row: Conflict & { dates: string[] }): string {
  const on = row.dates[0]

  /*
   * <b>The calendar, because that is where she can do something</b> (D-606).
   *
   * `asked`: <i>"the open her evening button should probably open it up on the
   * calendar instead of a parents view since the parents/dancers view
   * currently doesn't let tonya actually do anything to resolve it."</i> It
   * went to `/find`, which is a read-only picture of the dancer's day — every
   * lever that exists for a clash is on a class, and classes are clickable on
   * the calendar and not there.
   *
   * <b>Filtered to her, so it opens on her evening rather than the studio's.</b>
   * A Thursday holds dozens of blocks, and a link that lands on all of them
   * hands back the finding work the dialog had already done. Her name rides
   * along so the chip on the far side can say whose evening it is.
   *
   * <b>It also puts the destination back inside one capability.</b> `/find` is
   * `ViewPeople` while this screen is `ViewAllSchedules`, and D-5 grants those
   * separately — so the dancer row was the one destination its own reader could
   * be refused. `/calendar/day/:date` is `ViewAllSchedules` too, the same gate
   * `/conflicts` carries, so all three rows now land inside it.
   */
  if (row.kind === 'Dancer') {
    return `/calendar/day/${on}?wholeStudio=true`
      + `&dancer=${row.subjectId}`
      + `&dancerName=${encodeURIComponent(row.subject)}`
  }

  if (row.kind === 'Teacher') return `/calendar/teachers/${on}`

  return `/calendar/day/${on}?wholeStudio=true`
}

/** Whether this row's destination is one its reader may open. */
export function mayFollow(kind: Conflict['kind'], canViewPeople: boolean): boolean {
  /*
   * <b>Every row now lands on the calendar, so every reader of this screen may
   * follow every link</b> (D-606).
   *
   * The gate existed because the dancer row went to `/find`, which is
   * `ViewPeople` while this screen is `ViewAllSchedules` — D-5 grants those
   * separately, so somebody holding the first without the second was offered a
   * link the destination refused. All three destinations are now
   * `ViewAllSchedules`, the same gate `/conflicts` carries, so there is no
   * mismatch left to hide. That is the better half of D-333: decide it where
   * the permissions are known, and best of all do not create the split.
   *
   * <b>And `routes.test.ts` guards it better than this ever did</b>, by
   * asserting the pair agrees — a destination that later moves behind a
   * narrower capability fails there rather than silently drawing a link its
   * reader is refused.
   *
   * <b>Kept as a function rather than deleted.</b> It is the one place that
   * answers "may this reader follow this row", `routes.test.ts` names it as
   * such, and a destination added later will want to ask again.
   */
  void canViewPeople
  void kind
  return true
}

/**
 * The two blocks against the hour, with the overlap called out.
 *
 * <b>Drawn rather than described</b>, because "5:00&ndash;6:00 and 5:30&ndash;
 * 6:30 overlap 30 minutes" is three numbers to hold in your head and the
 * picture is none. Positioned by percentage across the span the pair occupies,
 * with a half hour of air either side so a block never touches the edge.
 */
function Strip({ first, second }: { first: ConflictSide; second: ConflictSide }) {
  const from = Math.min(toMinutes(first.start), toMinutes(second.start)) - 30
  const to = Math.max(toMinutes(first.end), toMinutes(second.end)) + 30
  const span = to - from

  const place = (side: ConflictSide) => ({
    left: `${((toMinutes(side.start) - from) / span) * 100}%`,
    width: `${((toMinutes(side.end) - toMinutes(side.start)) / span) * 100}%`,
  })

  const clash = {
    left: `${((Math.max(toMinutes(first.start), toMinutes(second.start)) - from) / span) * 100}%`,
    width: `${
      ((Math.min(toMinutes(first.end), toMinutes(second.end)) -
        Math.max(toMinutes(first.start), toMinutes(second.start))) /
        span) *
      100
    }%`,
  }

  return (
    <div className="mt-4 rounded-xl border border-line bg-surface-2 px-3 pt-2.5 pb-2">
      <div className="tabular flex justify-between text-[11px] text-ink-3">
        <span>{label(from)}</span>
        <span>{label(to)}</span>
      </div>

      <div className="relative mt-1.5 flex flex-col gap-1">
        <Block side={first} style={place(first)} tone="var(--color-cat-2)" />
        <Block side={second} style={place(second)} tone="var(--color-cat-1)" />

        {/*
          Over both lanes rather than in either, because the overlap belongs to
          the pair. `aria-hidden` — the minutes are said in words underneath,
          and a screen reader has no use for a coloured band.
        */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-[-3px] rounded-[3px] border-x-2 border-crit bg-crit/15"
          style={clash}
        />
      </div>
    </div>
  )
}

function Block({
  side,
  style,
  tone,
}: {
  side: ConflictSide
  style: { left: string; width: string }
  tone: string
}) {
  return (
    <div className="relative h-[22px] rounded-md bg-surface-3">
      <div
        className="absolute inset-y-0 overflow-hidden rounded-md px-2 text-[11px] leading-[22px] font-semibold text-white"
        style={{ ...style, background: tone }}
      >
        {side.label}
      </div>
    </div>
  )
}

function Side({ side }: { side: ConflictSide }) {
  return (
    <li className="flex items-start gap-2.5 rounded-xl border border-line px-3 py-2.5">
      <AlertTriangle size={13} strokeWidth={2.5} className="mt-1 shrink-0 text-crit" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold wrap-anywhere">{side.label}</p>
        <p className="tabular mt-0.5 text-[12px] text-ink-3">
          {clockTime(side.start)}
          {meridiem(side.start)}&ndash;{clockTime(side.end)}
          {meridiem(side.end)}
          {side.roomName && <> &middot; {side.roomName}</>}
        </p>
      </div>

      <span className="shrink-0 rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-ink-3 uppercase">
        {side.kind === 'Booking' ? 'Booking' : side.kind === 'Hold' ? 'Weekend hold' : 'Class'}
      </span>
    </li>
  )
}

function label(minutes: number): string {
  const hour = Math.floor(minutes / 60)
  const at = `${String(hour).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00`

  return `${clockTime(at)}${meridiem(at)}`
}
